import { useCallback, useEffect, useState } from 'react';
import type { MatchReplaceRule } from '../../../store/proxy-store';

export const pruneSelectionToCurrentRules = ({
  selectedRuleIds,
  rules,
}: {
  selectedRuleIds: ReadonlySet<string>;
  rules: readonly MatchReplaceRule[];
}): ReadonlySet<string> => {
  const currentIds = new Set(rules.map((rule) => rule.id));
  const prunedIds = [...selectedRuleIds].filter((id) => currentIds.has(id));

  return prunedIds.length === selectedRuleIds.size
    ? selectedRuleIds
    : new Set(prunedIds);
};

export function useMatchReplaceRulesSelection({
  rules,
}: {
  rules: readonly MatchReplaceRule[];
}) {
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    setSelectedRuleIds((previous) => {
      const pruned = pruneSelectionToCurrentRules({
        selectedRuleIds: previous,
        rules,
      });

      return pruned === previous ? previous : new Set(pruned);
    });
  }, [rules]);

  const toggleRuleSelected = useCallback(
    ({ ruleId, selected }: { ruleId: string; selected: boolean }) => {
      setSelectedRuleIds((prev) => {
        const next = new Set(prev);

        if (selected) {
          next.add(ruleId);
        } else {
          next.delete(ruleId);
        }

        return next;
      });
    },
    [],
  );

  const selectAll = useCallback(
    ({
      rules: selectableRules,
      selected,
    }: {
      rules: readonly MatchReplaceRule[];
      selected: boolean;
    }) => {
      if (selected) {
        setSelectedRuleIds(new Set(selectableRules.map((r) => r.id)));
      } else {
        setSelectedRuleIds(new Set());
      }
    },
    [],
  );

  const isRuleSelected = useCallback(
    (ruleId: string) => selectedRuleIds.has(ruleId),
    [selectedRuleIds],
  );

  const clearSelection = useCallback(() => {
    setSelectedRuleIds(new Set());
  }, []);

  const headerCheckboxState: boolean | 'indeterminate' =
    selectedRuleIds.size === 0 ? false : 'indeterminate';

  return {
    selectedRuleIds,
    selectedCount: selectedRuleIds.size,
    headerCheckboxState,
    isRuleSelected,
    toggleRuleSelected,
    selectAll,
    clearSelection,
  };
}
